import { useEffect, useState } from 'react';

import '../../../estilos/selector-de-anio.css';
import { anioTecho, ANIO_PISO, aniosCercanos, todosLosAnios } from '../formato.js';

/**
 * DE QUÉ AÑO · cuadrícula corta con «Ver todos los años» y campo para escribirlo
 * (incremento 22/22; RF-275, RN-769, RN-773; prototipo aprobado v45
 * `-presupuesto-anio`).
 *
 * POR QUÉ EXISTE: con el calendario extendido hay 37 años presupuestables, y uno
 * más cada 1 de enero. Deisy Paez eligió la opción (b) el 2026-10-15: a la vista
 * los años cercanos —el pasado, el de hoy y los cuatro que vienen— y el resto tras
 * un «Ver todos los años» que también se cierra. Lo habitual a un toque, lo lejano
 * a dos, y un año cualquiera se puede escribir.
 *
 * VIVE DENTRO DE LA TARJETA ANUAL DEL TABLERO (RN-773): no es una pantalla ni una
 * ruta: la acción de definir el año ya estaba ahí y lo único nuevo es elegir cuál.
 *
 * LO QUE ESTE COMPONENTE NO HACE Y NO DEBE HACER: no valida el año contra nada
 * suyo. El año se valida en el servidor contra el calendario sembrado (RN-769) y el
 * mensaje de un año fuera es el que ya existía. Aquí solo se ofrecen los años que
 * el servidor aceptaría, para no mandar a nadie contra una pared (RN-774: el techo
 * se deriva en `formato.js` y en ningún otro archivo del frontend).
 *
 * EL AÑO ELEGIDO SE QUEDA A LA VISTA aunque sea lejano: al cerrar la lista completa
 * sigue entre los cercanos, para no tener que buscarlo otra vez.
 *
 * LA MARCA DE VISTO es de los años que ya tienen presupuesto, y el dato llega
 * RESUELTO del servidor (`aniosConPresupuesto`, contratos-topes rev. 2): esta
 * pantalla no deduce cuáles son (RNF-053). Si el dato no llegó, se pinta sin marcas
 * y todo lo demás funciona igual —y la frase que las explica tampoco aparece, que
 * explicar una marca invisible sólo confunde—.
 */
export function SelectorDeAnio({
  anioEnCurso,
  anioElegido,
  alElegir,
  aniosConPresupuesto = [],
  datoDePrueba = 'selector-de-anio',
}) {
  const [verTodos, setVerTodos] = useState(false);
  const [textoDelAnio, setTextoDelAnio] = useState(() => String(anioElegido ?? ''));
  const [mensaje, setMensaje] = useState('');

  const cercanos = aniosCercanos(anioEnCurso, anioElegido);
  const techo = anioTecho(anioEnCurso);
  const todos = todosLosAnios(anioEnCurso);
  const conPresupuesto = new Set((aniosConPresupuesto ?? []).map(Number));

  /*
   * El campo sigue al año elegido, venga de donde venga: de un toque en la
   * cuadrícula o del año del mes que el tablero propone. Así nunca se queda
   * enseñando un año distinto del que dice el botón de la acción.
   */
  useEffect(() => {
    setTextoDelAnio(String(anioElegido ?? ''));
    setMensaje('');
  }, [anioElegido]);

  function escribirAnio(texto) {
    setTextoDelAnio(texto);

    const limpio = texto.trim();
    if (limpio === '') {
      setMensaje('Escribe un año, con sus cuatro cifras.');
      return;
    }

    if (!/^\d+$/.test(limpio)) {
      setMensaje('Escribe un año, con sus cuatro cifras.');
      return;
    }

    const anio = Number(limpio);
    if (anio < ANIO_PISO || anio > techo) {
      setMensaje('Ese año está fuera del rango disponible.');
      return;
    }

    setMensaje('');
    alElegir(anio);
  }

  function boton(anio) {
    const elegido = Number(anio) === Number(anioElegido);
    const esEnCurso = Number(anio) === Number(anioEnCurso);
    const yaTiene = conPresupuesto.has(Number(anio));

    /*
     * EL AÑO EN CURSO se anuncia «AAAA, este año» y la MARCA DE VISTO lleva su
     * propia palabra en el título, exactamente como el prototipo aprobado: así la
     * marca no queda contada sólo como glifo y color, y el año en curso conserva
     * el anuncio literal que pide CA-22-20, sin mezclarle nada más.
     */
    return (
      <button
        key={anio}
        type="button"
        className={`anio-opcion${elegido ? ' elegido' : ''}${esEnCurso ? ' en-curso' : ''}${yaTiene ? ' definido' : ''}`}
        aria-pressed={elegido}
        aria-label={esEnCurso ? `${anio}, este año` : undefined}
        title={yaTiene ? 'Ya tiene presupuesto' : undefined}
        onClick={() => alElegir(anio)}
        data-prueba={`${datoDePrueba}-${anio}`}
      >
        {anio}
      </button>
    );
  }

  const hayMarcas = conPresupuesto.size > 0;
  const fraseDeLaMarca = hayMarcas
    ? ' La marca de visto es de los años que ya tienen presupuesto.'
    : '';

  return (
    <div className="selector-de-anio" data-prueba={datoDePrueba}>
      <span className="etiqueta" id={`${datoDePrueba}-etiqueta`}>Años</span>

      <div
        className="anios-cercanos"
        role="group"
        aria-labelledby={`${datoDePrueba}-etiqueta`}
        data-prueba={`${datoDePrueba}-cercanos`}
      >
        {cercanos.map(boton)}
      </div>

      <button
        type="button"
        className={`ver-todos${verTodos ? ' abierto' : ''}`}
        aria-expanded={verTodos}
        onClick={() => setVerTodos((previo) => !previo)}
        data-prueba={`${datoDePrueba}-ver-todos`}
      >
        {verTodos ? 'Ver solo los años cercanos' : 'Ver todos los años'}
        <span className="flecha" aria-hidden="true">{verTodos ? ' ▾' : ' ▸'}</span>
      </button>

      {verTodos && (
        <div className="anios-todos" data-prueba={`${datoDePrueba}-todos`}>
          {/* El rango y el conteo son CALCULADOS, jamás pegados (RN-760, CA-22-20). */}
          <div className="encabezado" data-prueba={`${datoDePrueba}-todos-encabezado`}>
            Del {ANIO_PISO} al {techo} · {todos.length} años
          </div>
          <div className="anios-cercanos">{todos.map(boton)}</div>
        </div>
      )}

      <span className="ayuda" data-prueba={`${datoDePrueba}-ayuda`}>
        {verTodos
          ? `Toca el año que quieras.${fraseDeLaMarca}`
          : `${anioEnCurso} es el año en curso. Para un año más lejano, usa «Ver todos los años».${fraseDeLaMarca}`}
      </span>

      <label className="etiqueta" htmlFor={`${datoDePrueba}-campo`}>Escribir un año</label>
      <input
        id={`${datoDePrueba}-campo`}
        className={`campo-de-anio${mensaje ? ' mal' : ''}`}
        type="text"
        inputMode="numeric"
        maxLength={4}
        value={textoDelAnio}
        onChange={(evento) => escribirAnio(evento.target.value)}
        aria-describedby={`${datoDePrueba}-ayuda-campo`}
        aria-invalid={mensaje ? true : undefined}
        data-prueba={`${datoDePrueba}-campo`}
      />
      <span className="ayuda" id={`${datoDePrueba}-ayuda-campo`} data-prueba={`${datoDePrueba}-ayuda-campo`}>
        Del {ANIO_PISO} al {techo}.
      </span>
      {mensaje && (
        <span className="mensaje-de-error" role="alert" data-prueba={`${datoDePrueba}-mensaje`}>
          {mensaje}
        </span>
      )}
    </div>
  );
}
